import styled from "styled-components";
import {brand, fontFamilies, radii} from "theme/tokens";
import {PlanName} from "./CompactUpgradeDialog.styles";

/**
 * The tier picker inside the first-month offer dialog (kitecorp/transferify-ui#1448). Externalized
 * per the repo's styling convention; the component file stays markup + wiring.
 *
 * The paper, the two columns and the artwork panel are NOT defined here: they are the compact upgrade
 * dialog's (`CompactUpgradeDialog.styles`), so the two offer dialogs share one shell and one collapse
 * to the bottom sheet. The tier rows are the exit offer's (`OfferOption`). Only what is particular to
 * this offer lives in this file.
 */

/**
 * The offer's title, in the compact dialog's heading style. Without that dialog's reserved ✕ gutter:
 * this surface has no ✕ (Later, Escape and the scrim are its ways out), so the gutter would only
 * wrap a long campaign name early.
 */
export const Title = styled(PlanName)`
	margin-bottom: 12px;
	padding-right: 0;
`;

/**
 * The renewal terms. Not fine print in a legal sense but it must be READ, so it sits above the CTA
 * at body-adjacent size rather than beneath it in grey — a discount whose renewal price is a surprise
 * is the complaint this line exists to prevent.
 */
export const Terms = styled.p`
	margin: 14px 0 0;
	font-size: 12.5px;
	color: ${({theme}) => theme.tokens.ink2};
	text-wrap: pretty;
`;

/**
 * Opens the complete pricing table above the offer (kitecorp/transferify-ui#1637). A BUTTON styled
 * as the quietest link in the row: it explores rather than commits, so it must not compete with the
 * gold CTA — the same reasoning `Later` follows one control over.
 */
export const FullPricingLink = styled.button`
	margin-right: auto;
	padding: 0;
	border: none;
	align-self: center;
	background: none;
	font: 600 12.5px ${fontFamilies.sans};
	color: ${({theme}) => theme.tokens.ink2};
	text-align: left;
	text-decoration: underline;
	text-underline-offset: 2px;
	cursor: pointer;

	&:hover {
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * The three controls: the pricing link, "Maybe later" and the primary.
 *
 * <b>"Maybe later" stays beside the primary at every width</b> (owner, 2026-08-30, ui#1665), and the
 * LINK takes a line of its own above the pair — `flex: 1 1 100%` on the first child is the whole rule.
 * Left to wrap on its own the row broke in the wrong place: the link kept its space and the PRIMARY
 * dropped below "Maybe later", which put the least important control on the widest line and the most
 * important one last.
 *
 * That used to apply below 663px only, the viewport at which the old 600px paper stopped shrinking.
 * Beside the artwork panel the plans column is at most about 380px wide, too narrow for all three
 * controls on one row at any viewport, so the link's own line is now the layout at every width.
 */
export const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 16px;

	> :first-child {
		flex: 1 1 100%;
		margin-right: 0;
		margin-bottom: 6px;
	}
`;

/** The committing control, carrying the same gold fill the callout's Claim button wore. */
export const Continue = styled.button`
	height: 40px;
	padding: 0 18px;
	border: none;
	border-radius: ${radii.control};
	background: ${brand.gold};
	color: ${brand.goldInk};
	font: 700 13.5px ${fontFamilies.sans};
	cursor: pointer;
	transition: 0.16s;

	&:hover:not(:disabled) {
		background: ${brand.goldDark};
	}

	&:focus-visible {
		outline: 2px solid ${brand.gold};
		outline-offset: 2px;
	}

	&:disabled {
		opacity: 0.6;
		cursor: default;
	}
`;

/** Declining. Text-only, so it never competes with the fill beside it. */
export const Later = styled.button`
	height: 40px;
	padding: 0 14px;
	border: none;
	border-radius: ${radii.control};
	background: none;
	color: ${({theme}) => theme.tokens.ink2};
	font: 600 13.5px ${fontFamilies.sans};
	cursor: pointer;

	&:hover {
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * Something this dialog was asked to do did not work: the payment sheet could not be minted (ui#1665),
 * or no source could price the tiers (ui#1671).
 *
 * Body weight rather than a caption, and one style for both: a reader who pressed a button is owed a
 * sentence rather than fine print, and the two failures are the same kind of answer to the same reader.
 */
export const Failure = styled.p`
	margin: 0 0 12px;
	font: 600 13.5px ${fontFamilies.sans};
	color: ${brand.error};
	text-wrap: pretty;
`;

/**
 * The degraded state where the tier list would have been (ui#1671): the failure sentence and the one
 * control that can still change the outcome.
 *
 * Reached only when the live read failed AND this bundle carries no amounts to fall back on, which a
 * real build cannot produce — so it is deliberately plain rather than designed. What it must never be
 * is silent: the alternative is the dialog this ticket was filed about, which showed nothing and
 * disabled its primary.
 */
export const PricingUnavailable = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	/* No gap: {@link Failure} brings its own bottom margin, and adding one here spaced the two
	   controls by the sum of the pair rather than by either. */
	gap: 0;
`;

/** Ask again. Neutral rather than gold: it recovers the offer, it does not commit to it. */
export const RetryPricing = styled.button`
	height: 36px;
	padding: 0 14px;
	border: 1px solid ${({theme}) => theme.tokens.line};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink};
	font: 600 13px ${fontFamilies.sans};
	cursor: pointer;
	transition: 0.16s;

	&:hover {
		border-color: ${({theme}) => theme.tokens.ink3};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/** Placeholder while the three prices are being resolved. */
export const Loading = styled.p`
	margin: 0;
	font-size: 13px;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The campaign's changing facts, kept together directly under the title. */
export const CampaignFacts = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	margin: 0 0 12px;
`;

/** One api-owned scarcity or deadline fact, styled as offer context rather than as a warning. */
export const CampaignFact = styled.p`
	margin: 0;
	font: 600 12.5px ${fontFamilies.sans};
	color: ${({theme}) => theme.tokens.ink2};
`;
